import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Result,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
  SvgImage,
  ImageDiv,
  CopyImageButton,
  AlertDiv,
  DesktopOnlyDiv,
  MobileOnlyDiv,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { FaRegCopy } from 'react-icons/fa6'
import { MdError } from 'react-icons/md'
import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
// import { MdError } from 'react-icons/md'

export function MansoryCheckWall() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const mansoryGradient = 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)'
  const [wallL, setWallL] = useState('')
  const [wallT, setWallT] = useState('')
  const [wallH, setWallH] = useState('')
  const [parts, setParts] = useState([])
  const [fyk, setFyk] = useState('')
  const [fpk, setFpk] = useState('')
  const [fpkc, setFpkc] = useState('')
  const [ftk, setFtk] = useState('')
  const [gammam, setGammam] = useState(2)
  const [g, setG] = useState('')
  const [q, setQ] = useState('')
  const [m, setM] = useState('')
  const [selectedRebar, setSelectedRebar] = useState('Ø5.0')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'mansory-checkwall-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setWallL(entry.wallL)
      setWallT(entry.wallT)
      setWallH(entry.wallH)
      setParts(entry.parts || [])
      setFyk(entry.fyk)
      setFpk(entry.fpk)
      setFpkc(entry.fpkc)
      setFtk(entry.ftk)
      setGammam(entry.gammam || 2)
      setG(entry.g)
      setQ(entry.q)
      setM(entry.m)
      setResult(entry.result)
      setData(entry.data)
      setSelectedRebar(entry.selectedRebar)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.wallL = validateField('wallL', wallL)
    newErrors.wallT = validateField('wallT', wallT)
    newErrors.wallH = validateField('wallH', wallH)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.fpk = validateField('fpk', fpk)
    newErrors.fpkc = validateField('fpkc', fpkc)
    newErrors.ftk = validateField('ftk', ftk)
    newErrors.gammam = validateField('gammam', gammam)
    newErrors.g = validateField('g', g)
    newErrors.q = validateField('q', q)
    newErrors.m = validateField('m', m)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [wallL, wallT, wallH, fyk, fpk, fpkc, ftk, gammam, g, q, m, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  const addPart = () => {
    setParts([...parts, { l: '', x: '' }])
  }

  const removePart = (index) => {
    setParts(parts.filter((_, i) => i !== index))
  }

  const updatePart = (index, field, value) => {
    const newParts = [...parts]
    newParts[index][field] = Number(value)
    setParts(newParts)
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        wall: {
          l: wallL,
          t: wallT,
          h: wallH,
          parts: parts,
        },
        fyk,
        fpk,
        fpkc,
        ftk,
        gammam,
        g,
        q,
        m,
      }

      setData(data)

      const response = await api.post('/mansory/check-wall', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        wallL,
        wallT,
        wallH,
        parts,
        fyk,
        fpk,
        fpkc,
        ftk,
        gammam,
        g,
        q,
        m,
        result: response.data,
        data,
        selectedRebar,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      wallL,
      wallT,
      wallH,
      parts,
      fyk,
      fpk,
      fpkc,
      ftk,
      gammam,
      g,
      q,
      m,
      selectedRebar,
      result,
      data,
      isFormValid,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'checkwall-state.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setWallL(jsonData.wallL || jsonData.wall?.l)
        setWallT(jsonData.wallT || jsonData.wall?.t)
        setWallH(jsonData.wallH || jsonData.wall?.h)
        setParts(jsonData.parts || jsonData.wall?.parts || [])
        setFyk(jsonData.fyk)
        setFpk(jsonData.fpk)
        setFpkc(jsonData.fpkc)
        setFtk(jsonData.ftk)
        setGammam(jsonData.gammam || 2)
        setG(jsonData.g)
        setQ(jsonData.q)
        setM(jsonData.m)
        setSelectedRebar(jsonData.selectedRebar)
        setResult(jsonData.result || '')
        setData(jsonData.data || {})
        setIsFormValid(jsonData.isFormValid)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o arquivo. Verifique o formato.')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const wallImageRef = useRef(null)
  const leftWindImageRef = useRef(null)
  const rightWindImageRef = useRef(null)

  const WallImage = () => {
    const length = Number(wallL)
    const thicknessValue = Number(wallT)

    if (!Number.isFinite(length) || length <= 0 || !Number.isFinite(thicknessValue) || thicknessValue <= 0) {
      return null
    }

    const scale = 460 / length
    const thickness = scale * thicknessValue
    if (!Number.isFinite(thickness) || thickness <= 0) {
      return null
    }
    const startPosition = (500 - thickness) / 2

    return (
      <SvgImage ref={wallImageRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="500" height="500" fill="white" />

        <rect x="20" y={startPosition} width="460" height={thickness} fill="none" stroke="black" strokeWidth="3" />
        {parts.map((part, index) => {
          const partLength = Number(part.l)
          const partX = Number(part.x)
          if (!Number.isFinite(partLength) || !Number.isFinite(partX)) {
            return null
          }

          return (
            <rect
              key={index}
              x={20 + scale * partX - thickness / 2}
              y={startPosition + thickness}
              width={thickness}
              height={scale * partLength}
              fill="none"
              stroke="black"
              strokeWidth={3}
            />
          )
        })}
      </SvgImage>
    )
  }

  const LeftWindImage = () => {
    const scale = 105 / result.tension1.rebarCalculation.max
    const lineStartX = 20
    const lineStartY = 125 + scale * result.tension1.t1
    const lineEndX = 480
    const lineEndY = 20

    if (![scale, lineStartY].every(Number.isFinite)) {
      return null
    }

    return (
      <SvgImage
        style={{ height: '250' }}
        ref={leftWindImageRef}
        viewBox="0 0 500 250"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="500" height="250" fill="white" />
        <line x1={lineStartX} y1="125" x2={lineEndX} y2="125" stroke="black" strokeWidth="3" />
        <line x1={lineStartX} y1={lineStartY} x2={lineEndX} y2={lineEndY} stroke="black" strokeWidth="3" />
        <line x1={lineStartX} y1={lineStartY} x2={lineStartX} y2="125" stroke="black" strokeWidth="3" />
        <line x1={lineEndX} y1={lineEndY} x2={lineEndX} y2="125" stroke="black" strokeWidth="3" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x={lineStartX + 20} y={lineStartY + 30}>
            {(result.tension1.t1 * 1000).toFixed(1)}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x={lineEndX - 50} y={lineEndY}>
            {(result.tension1.rebarCalculation.max * 1000).toFixed(1)}
          </tspan>
        </text>
      </SvgImage>
    )
  }

  const RightWindImage = () => {
    const scale = 105 / result.tension2.rebarCalculation.max
    const lineStartX = 20
    const lineStartY = 20
    const lineEndX = 480
    const lineEndY = 125 + scale * result.tension1.t2

    if (![scale, lineStartY].every(Number.isFinite)) {
      return null
    }

    return (
      <SvgImage
        style={{ height: '250' }}
        ref={rightWindImageRef}
        viewBox="0 0 500 250"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="500" height="250" fill="white" />
        <line x1={lineStartX} y1="125" x2={lineEndX} y2="125" stroke="black" strokeWidth="3" />
        <line x1={lineStartX} y1={lineStartY} x2={lineEndX} y2={lineEndY} stroke="black" strokeWidth="3" />
        <line x1={lineStartX} y1={lineStartY} x2={lineStartX} y2="125" stroke="black" strokeWidth="3" />
        <line x1={lineEndX} y1={lineEndY} x2={lineEndX} y2="125" stroke="black" strokeWidth="3" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x={lineStartX + 40} y={lineStartY}>
            {(result.tension2.rebarCalculation.max * 1000).toFixed(1)}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x={lineEndX - 20} y={lineEndY + 25}>
            {(result.tension2.t1 * 1000).toFixed(1)}
          </tspan>
        </text>
      </SvgImage>
    )
  }

  const getSvgExportSize = (svgElement) => {
    const viewBox = svgElement.viewBox?.baseVal
    if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
      return { width: viewBox.width, height: viewBox.height }
    }

    const widthAttr = parseFloat(svgElement.getAttribute('width'))
    const heightAttr = parseFloat(svgElement.getAttribute('height'))
    if (Number.isFinite(widthAttr) && widthAttr > 0 && Number.isFinite(heightAttr) && heightAttr > 0) {
      return { width: widthAttr, height: heightAttr }
    }

    const rect = svgElement.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      return { width: rect.width, height: rect.height }
    }

    return { width: 500, height: 500 }
  }

  const handleCopySVG = async (svgRef) => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const exportSize = getSvgExportSize(svgElement)
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(exportSize.width)
      canvas.height = Math.round(exportSize.height)
      const context = canvas.getContext('2d')
      if (!context) {
        URL.revokeObjectURL(url)
        return
      }
      context.drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  return (
    <PageContainer>
      <PageHeader $gradient={mansoryGradient}>
        <div className="header-content">
          <BackButton to="/alvenaria">
            <FiArrowLeft /> Voltar para Alvenaria
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/alvenaria">Alvenaria</a>
            <span>/</span>
            <span>Verificação de Parede</span>
          </div>
          <h1>🧱 Verificação de Parede de Alvenaria Estrutural</h1>
          <p className="subtitle">
            Verificação de paredes de alvenaria estrutural considerando carregamentos e características da parede
          </p>
          <div className="badges">
            <Badge>📋 NBR 16868-1:2020</Badge>
            <Badge>🧮 Verificação Estrutural</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard>
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza a verificação de paredes de alvenaria estrutural, considerando as dimensões da
              parede, características dos materiais e carregamentos aplicados, conforme a NBR 16868-1:2020.
            </p>
            <p>
              O cálculo verifica a capacidade da parede de resistir aos esforços solicitantes, considerando aberturas e
              características geométricas específicas.
            </p>
            <div className="norms">
              <span>NBR 16868-1:2020</span>
              <span>Verificação de Paredes</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <h3>Geometria da Parede</h3>
          {wallL > 0 && wallT > 0 && (
            <ImageDiv>
              <WallImage />
              <CopyImageButton
                type="button"
                title="Clique para copiar essa imagem"
                onClick={() => handleCopySVG(wallImageRef)}
              >
                <FaRegCopy />
                Copiar
              </CopyImageButton>
            </ImageDiv>
          )}
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Comprimento (cm)</span>
              <ValidatedInput
                name="wallL"
                type="number"
                value={wallL || ''}
                onChange={(event) => setWallL(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Espessura (cm)</span>
              <ValidatedInput
                name="wallT"
                type="number"
                value={wallT || ''}
                onChange={(event) => setWallT(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Altura (cm)</span>
              <ValidatedInput
                name="wallH"
                type="number"
                value={wallH || ''}
                onChange={(event) => setWallH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Aba</h3>
            {parts.map((part, index) => (
              <div
                key={index}
                style={{ border: '1px solid #ddd', padding: '10px', marginBottom: '10px', borderRadius: '5px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <h4 style={{ margin: 0 }}>Aba {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => removePart(index)}
                    style={{
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      padding: '5px 10px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                  >
                    Remover
                  </button>
                </div>
                <InputDiv>
                  <span>Comprimento (cm)</span>
                  <input
                    type="number"
                    value={part.l || ''}
                    onChange={(e) => updatePart(index, 'l', e.target.value)}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Posição X (cm)</span>
                  <input
                    type="number"
                    value={part.x || ''}
                    onChange={(e) => updatePart(index, 'x', e.target.value)}
                    disabled={isLoading}
                  />
                </InputDiv>
              </div>
            ))}
            <button
              type="button"
              onClick={addPart}
              style={{
                marginBottom: '20px',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              + Adicionar aba
            </button>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência do Prisma (MPa)</span>
              <ValidatedInput
                name="fpk"
                type="number"
                value={fpk || ''}
                onChange={(event) => setFpk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência do Prisma Cheio (MPa)</span>
              <ValidatedInput
                name="fpkc"
                type="number"
                value={fpkc || ''}
                onChange={(event) => setFpkc(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência à Tração (MPa)</span>
              <ValidatedInput
                name="ftk"
                type="number"
                value={ftk || ''}
                onChange={(event) => setFtk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Coeficiente de Ponderação</h3>
            <InputDiv>
              <span>γm</span>
              <ValidatedInput
                name="gammam"
                type="number"
                value={gammam || ''}
                onChange={(event) => setGammam(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Cargas</h3>
            <InputDiv>
              <span>Carga Permanente (kN/m)</span>
              <ValidatedInput
                name="g"
                type="number"
                value={g || ''}
                onChange={(event) => setG(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Carga Variável (kN/m)</span>
              <ValidatedInput
                name="q"
                type="number"
                value={q || ''}
                onChange={(event) => setQ(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Esforços</h3>
            <InputDiv>
              <span>Momento (kN.m)</span>
              <ValidatedInput
                name="m"
                type="number"
                value={m || ''}
                onChange={(event) => setM(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Verificação da compressão</h3>
              <MathDiv>
                <h4>Bordo esquerdo</h4>
                {!result.compressive1.groutRequested && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} \\geq ${(result.compressive1.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Resiste sem graute.</p>
                  </>
                )}
                {result.compressive1.groutRequested && result.compressive1.groutSolves && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} < ${(result.compressive1.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Necessário adicionar graute.</p>
                    <p>Comprimento a partir do bordo que deve ser grauteado:</p>
                    <BlockMath math={`l = ${(result.compressive1.groutPosition.l * 100).toFixed(1)} \\text{ cm}`} />
                  </>
                )}
                {result.compressive1.groutRequested && !result.compressive1.groutSolves && (
                  <>
                    <BlockMath
                      math={`{f_{pk}}^* = ${data.fpkc} \\text{ MPa} < ${(result.compressive1.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Impossível de resolver mesmo com adição de graute.</p>
                  </>
                )}
                <h4>Bordo direito</h4>
                {!result.compressive2.groutRequested && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} \\geq ${(result.compressive2.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Resiste sem graute.</p>
                  </>
                )}
                {result.compressive2.groutRequested && result.compressive2.groutSolves && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} < ${(result.compressive2.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Necessário adicionar graute.</p>
                    <p>Comprimento a partir do bordo que deve ser grauteado:</p>
                    <BlockMath math={`l = ${(result.compressive2.groutPosition.l * 100).toFixed(1)} \\text{ cm}`} />
                  </>
                )}
                {result.compressive2.groutRequested && !result.compressive2.groutSolves && (
                  <>
                    <BlockMath
                      math={`{f_{pk}}^* = ${data.fpkc} \\text{ MPa} < ${(result.compressive2.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Impossível de resolver mesmo com adição de graute.</p>
                  </>
                )}
              </MathDiv>
              <h3>Verificação da tração</h3>
              <MathDiv>
                <h4>Bordo esquerdo</h4>
                {!result.tension1.rebarRequested ? (
                  <>
                    <BlockMath math={`${(result.tension1.t1 * 1000).toFixed(1)} \\leq ${result.tension1.t2 * 1000}`} />
                    <p>Resiste sem armadura.</p>
                  </>
                ) : (
                  <>
                    <AlertDiv>
                      <BlockMath math={`${(result.tension1.t1 * 1000).toFixed(1)} > ${result.tension1.t2 * 1000}`} />
                      <MdError />
                    </AlertDiv>
                    <p>Necessário adicionar armadura.</p>
                    <BlockMath
                      math={`A_s = \\dfrac{F}{0.5 \\cdot f_{yd}} = \\dfrac{${result.tension1.rebarCalculation.totalForce.toFixed(1)}}{${result.tension1.rebarCalculation.stress.toFixed(2)}} = ${result.tension1.rebarCalculation.ascalc.toFixed(2)} \\text{ cm}^2`}
                    />
                  </>
                )}
                <h4>Bordo direito</h4>
                {!result.tension2.rebarRequested ? (
                  <>
                    <BlockMath math={`${(result.tension2.t1 * 1000).toFixed(1)} \\leq ${result.tension2.t2 * 1000}`} />
                    <p>Resiste sem armadura.</p>
                  </>
                ) : (
                  <>
                    <AlertDiv>
                      <BlockMath math={`${(result.tension2.t1 * 1000).toFixed(1)} > ${result.tension2.t2 * 1000}`} />
                      <MdError />
                    </AlertDiv>
                    <p>Necessário adicionar armadura.</p>
                    <BlockMath
                      math={`A_s = \\dfrac{F}{0.5 \\cdot f_{yd}} = \\dfrac{${result.tension2.rebarCalculation.totalForce.toFixed(1)}}{${result.tension2.rebarCalculation.stress.toFixed(2)}} = ${result.tension2.rebarCalculation.ascalc.toFixed(2)} \\text{ cm}^2`}
                    />
                  </>
                )}
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Propriedades geométricas</h3>
              <MathDiv>
                <BlockMath math={`A= ${result.area} \\text{ cm}^2`} />
                <BlockMath math={`I= ${result.inertia.toFixed(0)} \\text{ cm}^4`} />
                <BlockMath math={`x_{cg}= ${(result._center * 100).toFixed(0)} \\text{ cm}`} />
              </MathDiv>
              <h3>Carregamentos</h3>
              <MathDiv>
                <BlockMath
                  math={`G= \\dfrac{g}{t} = \\dfrac{${data.g}}{${data.wall.t / 100}} = ${result.sigmaG.toFixed(2)} \\text{ kN/cm}^2`}
                />
                <BlockMath
                  math={`Q= \\dfrac{q}{t} = \\dfrac{${data.q}}{${data.wall.t / 100}} = ${result.sigmaQ.toFixed(2)} \\text{ kN/cm}^2`}
                />
                <MobileOnlyDiv>
                  <BlockMath
                    math={`V_{esquerda}= \\dfrac{M\\cdot y}{I} = \\dfrac{${data.m} \\cdot ${result._center.toFixed(3)}}{${(result.inertia / 100000000).toFixed(5)}}`}
                  />
                  <BlockMath math={`V_{esquerda}= ${result.sigmaV1.toFixed(2)} \\text{ kN/cm}^2`} />
                  <BlockMath
                    math={`V_{direita}= \\dfrac{M\\cdot y}{I} = \\dfrac{${data.m} \\cdot ${(data.wall.l / 100 - result._center).toFixed(3)}}{${(result.inertia / 100000000).toFixed(5)}}`}
                  />
                  <BlockMath math={`V_{direita} = ${result.sigmaV2.toFixed(2)} \\text{ kN/cm}^2`} />
                </MobileOnlyDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`V_{esquerda}= \\dfrac{M\\cdot y}{I} = \\dfrac{${data.m} \\cdot ${result._center.toFixed(3)}}{${(result.inertia / 100000000).toFixed(5)}} = ${result.sigmaV1.toFixed(2)} \\text{ kN/cm}^2`}
                  />
                  <BlockMath
                    math={`V_{direita}= \\dfrac{M\\cdot y}{I} = \\dfrac{${data.m} \\cdot ${(data.wall.l / 100 - result._center).toFixed(3)}}{${(result.inertia / 100000000).toFixed(5)}} = ${result.sigmaV2.toFixed(2)} \\text{ kN/cm}^2`}
                  />
                </DesktopOnlyDiv>
              </MathDiv>
              <h3>Verificação da compressão</h3>
              <MathDiv>
                <h4>Bordo esquerdo</h4>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{0.7 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 2.67 \\cdot V = \\dfrac{0.7 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive1.r.toFixed(3)}} + 2.67 \\cdot ${result.sigmaV1.toFixed(2)}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{0.7 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 2.67 \\cdot V`}
                  />
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{0.7 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive1.r.toFixed(3)}} + \\cdots`}
                  />
                  <BlockMath math={`\\cdots + 2.67 \\cdot ${result.sigmaV1.toFixed(2)}`} />
                </MobileOnlyDiv>
                <BlockMath
                  math={`f_{pk} \\geq ${result.compressive1.c1.toFixed(2)} \\text{ kN/m}^2 = ${(result.compressive1.c1 / 1000).toFixed(2)} \\text{ MPa}`}
                />
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{1.4 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 1.6 \\cdot V = \\dfrac{1.4 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive1.r.toFixed(3)}} + 1.6 \\cdot ${result.sigmaV1.toFixed(2)}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{1.4 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 1.6 \\cdot V `}
                  />
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{1.4 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive1.r.toFixed(3)}} + \\cdots`}
                  />
                  <BlockMath math={`\\cdots + 1.6 \\cdot ${result.sigmaV1.toFixed(2)}`} />
                </MobileOnlyDiv>
                <BlockMath
                  math={`f_{pk} \\geq ${result.compressive1.c1.toFixed(2)} \\text{ kN/m}^2 = ${(result.compressive1.c2 / 1000).toFixed(2)} \\text{ MPa}`}
                />
                {!result.compressive1.groutRequested && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} \\geq ${(result.compressive1.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Resiste sem graute.</p>
                  </>
                )}
                {result.compressive1.groutRequested && result.compressive1.groutSolves && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} < ${(result.compressive1.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Necessário adicionar graute.</p>
                    <p>
                      Encontrando o limite de V para o f<sub>pk</sub> resistir:
                    </p>
                    {result.compressive1.groutPosition.caseId === 1 ? (
                      <>
                        <BlockMath
                          math={`V_{lim} = \\dfrac{\\dfrac{0.7 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} - f_{pk}}{-2.67}`}
                        />
                        <BlockMath
                          math={`V_{lim} = \\frac{\\dfrac{0.7 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive1.r}} - ${data.fpk * 1000}}{-2.67}`}
                        />
                      </>
                    ) : (
                      <>
                        <BlockMath
                          math={`V_{lim} = \\dfrac{\\dfrac{1.4 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} - f_{pk}}{-1.6}`}
                        />
                        <BlockMath
                          math={`V_{lim} = \\dfrac{\\dfrac{1.4 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive1.r}} - ${data.fpk * 1000}}{-1.6}`}
                        />
                      </>
                    )}
                    <BlockMath
                      math={`V_{lim} = ${result.compressive1.groutPosition.aMin.toFixed(1)} \\text{ kN/m}^2`}
                    />
                    <p>Comprimento a partir do bordo que deve ser grauteado:</p>
                    <BlockMath math={`l = ${(result.compressive1.groutPosition.l * 100).toFixed(1)} \\text{ cm}`} />
                  </>
                )}
                {result.compressive1.groutRequested && !result.compressive1.groutSolves && (
                  <>
                    <BlockMath
                      math={`{f_{pk}}^* = ${data.fpkc} \\text{ MPa} < ${(result.compressive1.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Impossível de resolver mesmo com adição de graute.</p>
                  </>
                )}
                <h4>Bordo direito</h4>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{0.7 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 2.67 \\cdot V = \\dfrac{0.7 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive2.r.toFixed(3)}} + 2.67 \\cdot ${result.sigmaV2.toFixed(2)}`}
                  />
                  <BlockMath
                    math={`f_{pk} \\geq ${result.compressive2.c1.toFixed(2)} \\text{ kN/m}^2 = ${(result.compressive2.c1 / 1000).toFixed(2)} \\text{ MPa}`}
                  />
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{1.4 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 1.6 \\cdot V = \\dfrac{1.4 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive2.r.toFixed(3)}} + 1.6 \\cdot ${result.sigmaV2.toFixed(2)}`}
                  />
                  <BlockMath
                    math={`f_{pk} \\geq ${result.compressive2.c2.toFixed(2)} \\text{ kN/m}^2 = ${(result.compressive2.c2 / 1000).toFixed(2)} \\text{ MPa}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{0.7 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 2.67 \\cdot V`}
                  />
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{0.7 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive2.r.toFixed(3)}} + \\cdots`}
                  />
                  <BlockMath math={`\\cdots + 2.67 \\cdot ${result.sigmaV2.toFixed(2)}`} />
                  <BlockMath
                    math={`f_{pk} \\geq ${result.compressive2.c1.toFixed(2)} \\text{ kN/m}^2 = ${(result.compressive2.c1 / 1000).toFixed(2)} \\text{ MPa}`}
                  />
                  <BlockMath math={`f_{pk} \\geq \\dfrac{1.4 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} + 1.6 \\cdot V`} />
                  <BlockMath
                    math={`f_{pk} \\geq \\dfrac{1.4 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive2.r.toFixed(3)}} + \\cdots`}
                  />
                  <BlockMath math={`\\cdots + 1.6 \\cdot ${result.sigmaV2.toFixed(2)}`} />
                  <BlockMath
                    math={`f_{pk} \\geq ${result.compressive2.c2.toFixed(2)} \\text{ kN/m}^2 = ${(result.compressive2.c2 / 1000).toFixed(2)} \\text{ MPa}`}
                  />
                </MobileOnlyDiv>
                {!result.compressive2.groutRequested && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} \\geq ${(result.compressive2.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Resiste sem graute.</p>
                  </>
                )}
                {result.compressive2.groutRequested && result.compressive2.groutSolves && (
                  <>
                    <BlockMath
                      math={`f_{pk} = ${data.fpk} \\text{ MPa} < ${(result.compressive2.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Necessário adicionar graute.</p>
                    <p>
                      Encontrando o limite de V para o f<sub>pk</sub> resistir:
                    </p>
                    {result.compressive2.groutPosition.caseId === 1 ? (
                      <>
                        <BlockMath
                          math={`V_{lim} = \\dfrac{\\dfrac{0.7 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} - f_{pk}}{-2.67}`}
                        />
                        <BlockMath
                          math={`V_{lim} = \\frac{\\dfrac{0.7 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive2.r}} - ${data.fpk * 1000}}{-2.67}`}
                        />
                      </>
                    ) : (
                      <>
                        <BlockMath
                          math={`V_{lim} = \\dfrac{\\dfrac{1.4 \\cdot Q + 1.4 \\cdot G}{0.35 \\cdot R} - f_{pk}}{-1.6}`}
                        />
                        <BlockMath
                          math={`V_{lim} = \\dfrac{\\dfrac{1.4 \\cdot ${result.sigmaQ.toFixed(2)} + 1.4 \\cdot ${result.sigmaG.toFixed(2)}}{0.35 \\cdot ${result.compressive2.r}} - ${data.fpk * 1000}}{-1.6}`}
                        />
                      </>
                    )}
                    <BlockMath
                      math={`V_{lim} = ${result.compressive2.groutPosition.aMin.toFixed(1)} \\text{ kN/m}^2`}
                    />
                    <p>Comprimento a partir do bordo que deve ser grauteado:</p>
                    <BlockMath math={`l = ${(result.compressive2.groutPosition.l * 100).toFixed(1)} \\text{ cm}`} />
                  </>
                )}
                {result.compressive2.groutRequested && !result.compressive2.groutSolves && (
                  <>
                    <BlockMath
                      math={`{f_{pk}}^* = ${data.fpkc} \\text{ MPa} < ${(result.compressive2.cMax / 1000).toFixed(2)} \\text{ MPa}`}
                    />
                    <p>Impossível de resolver mesmo com adição de graute.</p>
                  </>
                )}
              </MathDiv>
              <h3>Verificação da tração</h3>
              <MathDiv>
                <h4>Bordo esquerdo</h4>
                <BlockMath math={`1.4 \\cdot V - 0.9 \\cdot G \\leq \\dfrac{f_{tk}}{\\gamma_m}`} />
                <BlockMath
                  math={`1.4 \\cdot ${result.sigmaV1.toFixed(2)} - 0.9 \\cdot ${result.sigmaG.toFixed(2)} \\leq \\dfrac{${data.ftk}}{${data.gammam}}`}
                />
                {!result.tension1.rebarRequested ? (
                  <>
                    <BlockMath math={`${(result.tension1.t1 * 1000).toFixed(1)} \\leq ${result.tension1.t2 * 1000}`} />
                    <p>Resiste sem armadura.</p>
                  </>
                ) : (
                  <>
                    <AlertDiv>
                      <BlockMath math={`${(result.tension1.t1 * 1000).toFixed(1)} > ${result.tension1.t2 * 1000}`} />
                      <MdError />
                    </AlertDiv>
                    <p>Necessário adicionar armadura.</p>
                    <BlockMath math={`1.4 \\cdot V + 0.9 \\cdot G`} />
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`1.4 \\cdot ${result.sigmaV2.toFixed(2)} + 0.9 \\cdot ${result.sigmaG.toFixed(2)} = ${(result.tension1.rebarCalculation.max * 1000).toFixed(1)} \\text{ kN/m}^2`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath
                        math={`1.4 \\cdot ${result.sigmaV2.toFixed(2)} + 0.9 \\cdot ${result.sigmaG.toFixed(2)}`}
                      />
                      <BlockMath math={`${(result.tension1.rebarCalculation.max * 1000).toFixed(1)} \\text{ kN/m}^2`} />
                    </MobileOnlyDiv>
                    <ImageDiv>
                      <LeftWindImage />
                      <CopyImageButton
                        type="button"
                        title="Clique para copiar essa imagem"
                        onClick={() => handleCopySVG(leftWindImageRef)}
                      >
                        <FaRegCopy />
                        Copiar
                      </CopyImageButton>
                    </ImageDiv>
                    <p>Comprimento tracionado</p>
                    <BlockMath math={`x = ${result.tension1.rebarCalculation.x.toFixed(3)} \\text{ m}`} />
                    <p>Força de tração na alma</p>
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`F_{alma} = \\dfrac{x \\cdot \\sigma}{2} \\cdot t = \\dfrac{${result.tension1.rebarCalculation.x.toFixed(3)} \\cdot ${(result.tension1.t1 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} = ${(result.tension1.rebarCalculation.totalForce - result.tension1.rebarCalculation.totalPartsForce).toFixed(2)} \\text{ kN}`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`F_{alma} = \\dfrac{x \\cdot \\sigma}{2} \\cdot t`} />
                      <BlockMath
                        math={`F_{alma} = \\dfrac{${result.tension1.rebarCalculation.x.toFixed(3)} \\cdot ${(result.tension1.t1 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} = ${(result.tension1.rebarCalculation.totalForce - result.tension1.rebarCalculation.totalPartsForce).toFixed(2)} \\text{ kN}`}
                      />
                    </MobileOnlyDiv>
                    <p>Força de tração nas abas</p>
                    {result.tension1.rebarCalculation.partsForce.map((p, i) => (
                      <>
                        <DesktopOnlyDiv>
                          <BlockMath
                            math={`F_{aba,${i + 1}} = \\dfrac{\\sigma_1 + \\sigma_2}{2} \\cdot t \\cdot L = \\dfrac{${(p.stress1 * 1000).toFixed(2)} + ${(p.stress2 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} \\cdot ${p.l / 100} = ${p.sum.toFixed(2)} \\text{ kN}`}
                          />
                        </DesktopOnlyDiv>
                        <MobileOnlyDiv>
                          <BlockMath math={`F_{aba,${i + 1}} = \\dfrac{\\sigma_1 + \\sigma_2}{2} \\cdot t \\cdot L`} />
                          <BlockMath
                            math={`F_{aba,${i + 1}} = \\dfrac{${(p.stress1 * 1000).toFixed(2)} + ${(p.stress2 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} \\cdot ${p.l / 100}`}
                          />
                          <BlockMath math={`F_{aba,${i + 1}} = ${p.sum.toFixed(2)} \\text{ kN}`} />
                        </MobileOnlyDiv>
                      </>
                    ))}
                    <BlockMath
                      math={`F = F_{alma} + \\sum F_{abas} = ${result.tension1.rebarCalculation.totalForce.toFixed(1)} \\text{ kN}`}
                    />
                    <BlockMath
                      math={`A_s = \\dfrac{F}{0.5 \\cdot f_{yd}} = \\dfrac{${result.tension1.rebarCalculation.totalForce.toFixed(1)}}{${result.tension1.rebarCalculation.stress.toFixed(2)}} = ${result.tension1.rebarCalculation.ascalc.toFixed(2)} \\text{ cm}^2`}
                    />
                  </>
                )}
                <h4>Bordo direito</h4>
                <BlockMath math={`1.4 \\cdot V - 0.9 \\cdot G \\leq \\dfrac{f_{tk}}{\\gamma_m}`} />
                <BlockMath
                  math={`1.4 \\cdot ${result.sigmaV2.toFixed(2)} - 0.9 \\cdot ${result.sigmaG.toFixed(2)} \\leq \\dfrac{${data.ftk}}{${data.gammam}}`}
                />
                {!result.tension2.rebarRequested ? (
                  <>
                    <BlockMath math={`${(result.tension2.t1 * 1000).toFixed(1)} \\leq ${result.tension2.t2 * 1000}`} />
                    <p>Resiste sem armadura.</p>
                  </>
                ) : (
                  <>
                    <AlertDiv>
                      <BlockMath math={`${(result.tension2.t1 * 1000).toFixed(1)} > ${result.tension2.t2 * 1000}`} />
                      <MdError />
                    </AlertDiv>
                    <p>Necessário adicionar armadura.</p>
                    <BlockMath math={`1.4 \\cdot V + 0.9 \\cdot G`} />
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`1.4 \\cdot ${result.sigmaV1.toFixed(2)} + 0.9 \\cdot ${result.sigmaG.toFixed(2)} = ${(result.tension2.rebarCalculation.max * 1000).toFixed(1)} \\text{ kN/m}^2`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath
                        math={`1.4 \\cdot ${result.sigmaV1.toFixed(2)} + 0.9 \\cdot ${result.sigmaG.toFixed(2)}`}
                      />
                      <BlockMath math={`${(result.tension2.rebarCalculation.max * 1000).toFixed(1)} \\text{ kN/m}^2`} />
                    </MobileOnlyDiv>
                    <ImageDiv>
                      <RightWindImage />
                      <CopyImageButton
                        type="button"
                        title="Clique para copiar essa imagem"
                        onClick={() => handleCopySVG(rightWindImageRef)}
                      >
                        <FaRegCopy />
                        Copiar
                      </CopyImageButton>
                    </ImageDiv>
                    <p>Comprimento tracionado</p>
                    <BlockMath math={`x = ${result.tension2.rebarCalculation.x.toFixed(3)} \\text{ m}`} />
                    <p>Força de tração na alma</p>
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`F_{alma} = \\dfrac{x \\cdot \\sigma}{2} \\cdot t = \\dfrac{${result.tension2.rebarCalculation.x.toFixed(3)} \\cdot ${(result.tension2.t1 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} = ${(result.tension2.rebarCalculation.totalForce - result.tension2.rebarCalculation.totalPartsForce).toFixed(2)} \\text{ kN}`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`F_{alma} = \\dfrac{x \\cdot \\sigma}{2} \\cdot t`} />
                      <BlockMath
                        math={`F_{alma} = \\dfrac{${result.tension2.rebarCalculation.x.toFixed(3)} \\cdot ${(result.tension2.t1 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} = ${(result.tension2.rebarCalculation.totalForce - result.tension2.rebarCalculation.totalPartsForce).toFixed(2)} \\text{ kN}`}
                      />
                    </MobileOnlyDiv>
                    <p>Força de tração nas abas</p>
                    {result.tension2.rebarCalculation.partsForce.map((p, i) => (
                      <>
                        <DesktopOnlyDiv>
                          <BlockMath
                            math={`F_{aba,${i + 1}} = \\dfrac{\\sigma_1 + \\sigma_2}{2} \\cdot t \\cdot L = \\dfrac{${(p.stress1 * 1000).toFixed(2)} + ${(p.stress2 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} \\cdot ${p.l / 100} = ${p.sum.toFixed(2)} \\text{ kN}`}
                          />
                        </DesktopOnlyDiv>
                        <MobileOnlyDiv>
                          <BlockMath math={`F_{aba,${i + 1}} = \\dfrac{\\sigma_1 + \\sigma_2}{2} \\cdot t \\cdot L`} />
                          <BlockMath
                            math={`F_{aba,${i + 1}} = \\dfrac{${(p.stress1 * 1000).toFixed(2)} + ${(p.stress2 * 1000).toFixed(2)}}{2} \\cdot ${data.wall.t / 100} \\cdot ${p.l / 100}`}
                          />
                          <BlockMath math={`F_{aba,${i + 1}}  = ${p.sum.toFixed(2)} \\text{ kN}`} />
                        </MobileOnlyDiv>
                      </>
                    ))}
                    <BlockMath
                      math={`F = F_{alma} + \\sum F_{abas} = ${result.tension2.rebarCalculation.totalForce.toFixed(1)} \\text{ kN}`}
                    />
                    <BlockMath
                      math={`A_s = \\dfrac{F}{0.5 \\cdot f_{yd}} = \\dfrac{${result.tension2.rebarCalculation.totalForce.toFixed(1)}}{${result.tension2.rebarCalculation.stress.toFixed(2)}} = ${result.tension2.rebarCalculation.ascalc.toFixed(2)} \\text{ cm}^2`}
                    />
                  </>
                )}
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
